import { ValidBusinessObject, PossibleBusinessObject } from '@iotv/iotv-v3-types'
import config from '@iotv/config'
import { v4 as uuidv4 } from 'uuid'
import { ModelCommon } from './model/ModelCommon'

import * as ModelClasses from './model'
import { IBusinessObjectAssembler } from './types/BOAType'

// // import {  ModelTypes } from './classDependentTypes';
type ModelTypes = { new <T>(...args: any[]): T }
type ModelClassNames = keyof ModelTypes

const debug = process.env.REACT_APP_DEBUG === 'false' && !process.env.AWS_SESSION_TOKEN && false

const filter = (ModelClasses: any) => {
  const filtered = Object.entries(ModelClasses).filter(([k, c]) => {
    const descriptor = Object.getOwnPropertyDescriptor(ModelClasses, k)
    return descriptor?.get ? descriptor.get().prototype instanceof ModelCommon : false
  })

  const filteredModel = Object.fromEntries(filtered)
  debug && console.log(`Assembler filtered `, filteredModel)
  debug && console.log(`From: `, ModelClasses)
}

const createBusinessObjectAssembler = (ModelClasses: any) => createBusinessObjectAssemblerFiltered(/*filter(ModelClasses) */ ModelClasses)

const createBusinessObjectAssemblerFiltered = (ModelClasses: any) =>
  class BusinessObjectAssembler implements IBusinessObjectAssembler {
    constructor() {
      debug && console.log('Model Classes', ModelClasses)
    }

    getClass(item: PossibleBusinessObject | string): ModelTypes | undefined {
      if (typeof item === 'object' && item.type) return item.type ? ModelClasses[item.type as ModelClassNames] : undefined
      else if (typeof item === 'string') return ModelClasses[item as ModelClassNames] ? ModelClasses[item as ModelClassNames] : undefined
      else return undefined
    }

    isInstance(item: PossibleBusinessObject): boolean {
      return this.getClass(item) !== undefined
    }

    assignNewId(item: PossibleBusinessObject): PossibleBusinessObject {
      debug && console.log('Item in assign new id')
      if (!item.id) item.id = uuidv4()
      return item
    }

    // getInstance is overloaded to allow pojos {id: 'xx', type: 'someType'} or just return clone if class instance already
    getInstance(item: PossibleBusinessObject): ValidBusinessObject | undefined {
      debug && console.log('Object in assembler', item)
      if (item === undefined) throw new Error('No item was provided to BusinessObjectAssembler getInstance\n item === undfined')
      if (item.type === undefined) throw new Error('No type on: ' + JSON.stringify(item))
      if (!item.id) this.assignNewId(item)
      const clazz = this.getClass(item)
      let instance = undefined
      if (clazz !== undefined) {
        if (item.sk && item.pk) {
          if (item instanceof clazz) {
            debug && console.log('item already instance of ', clazz.name)
            instance = item
          } else {
            debug && console.log(`item not an instance of ${clazz.name} but is possible`, item)
            instance = new clazz(item)
            debug && console.log('item Created ', instance)
          }
        } else {
          debug && console.log('No pk and sk for ', item)
          if (item.id && clazz.prototype) {
            instance = new clazz(item)
            debug && console.log('item Created ', clazz.name)
          }
        }
      } else {
        debug && console.log('No class for ', item.type ?? item.sk)
        debug && console.log('classes are', Object.keys(ModelClasses))
        if (debug && Object.keys(ModelClasses).includes(item.type)) {
          console.log(`Model[${item.type}] is`, ModelClasses[item.type as ModelClassNames])
        }
        debug && console.log('WARN: Allowing override model', item.type)
        instance = new ModelCommon(item.type, item)
      }
      debug && console.log('assembler returning ', instance)
      return instance as ValidBusinessObject
    }

    // getUserAttributes for a non type object
    getUserAttributes = (ob: PossibleBusinessObject) => {
      let userAttributes: { [k: string]: any } = {}
      Object.entries(ob).forEach(([k, v]) => {
        let type = 'String'
        if (typeof v === 'number') type = 'Number'
        else if (typeof v === 'string') type = 'String'
        else if (typeof v === 'boolean') type = 'Boolean'
        else if (v && v instanceof Object) {
          if (v instanceof Date) type = 'Date'
        }
        userAttributes[k] = { type }
      })
      return userAttributes
    }

    getSafeId(item: PossibleBusinessObject) {
      return item.type && item.id ? item.type + ':' + item.id : null
    }

    getInstanceFromPrimaryKey(pk: string, others?: Record<string, any>): ValidBusinessObject | undefined {
      const zombieInstance = this.getZombieFromPrimaryKey(pk, others)
      return this.getInstance(zombieInstance)
    }

    getZombieFromPrimaryKey = (pk: string, others?: Record<string, any>): ValidBusinessObject => {
      const [type, ...idOthers] = pk.split(':')
      const id = idOthers.join(':')
      const zombieInstance = { type, id, sk: `${type}:${id}`, pk: `${type}:${id}`, ...others }
      return zombieInstance
    }

    ghostify = <T extends { type: string }>(vob: T): T & ValidBusinessObject => {
      const newKeys = `${vob.type}:${config.app.ghostUuid}`
      return { ...vob, sk: newKeys, pk: newKeys, id: config.app.ghostUuid }
    }

    reidentify = <T extends { type: string }>(vob: T, id: string): T & ValidBusinessObject => {
      const newKeys = `${vob.type}:${id}`
      const newZombie = { ...vob, sk: newKeys, pk: newKeys, id }
      const instance = this.getInstance(newZombie)
      return (instance as T & ValidBusinessObject) ?? newZombie
    }

    getDynamoKeyFromPrimaryKey(pk: string) {
      const zombieInstance = { pk, sk: pk }
      return zombieInstance
    }

    isThingSubType(typeId: string) {
      //because we don't expect some clever prototype reflection to work in Webpack
      return ['Horse', 'Boat', 'etc'].includes(typeId) //TODO either move to config or do by reflection
    }

    // where ob has key and key is an array or similar obs
    flatten(ob: { [k: string]: any }, key: string, res: { [k: string]: any }[] = []) {
      if (ob[key] instanceof Array) {
        ob[key].forEach((ob: { [k: string]: any }) => this.flatten(ob, key, res))
      }
      res.push(ob)
      return res
    }

    // probably like flat, except need to provide initial res array
    flattenArray(x: { [k: string]: any }, res: { [k: string]: any }[]) {
      if (x instanceof Array) {
        x.forEach((y) => this.flattenArray(y, res))
      } else {
        res.push(x)
      }
    }

    assembleFromArray(arr: PossibleBusinessObject[]) {
      arr = arr.map((item) => this.getInstance(item)).filter((item) => item !== undefined) as ValidBusinessObject[]
      let top: { [k: string]: any } | undefined | null = arr.find((item) => item.pk === item.sk)
      top = top === undefined ? null : top
      arr.sort((a, b) => (a.name < b.name ? 0 : 1))
      if (top)
        arr.forEach((item) => {
          //console.log('Item in assemble: ', item)
          if (item.pk !== item.sk) {
            const typeArrayName = item.type
            if (top && top[typeArrayName] === undefined) top[typeArrayName] = []
            if (top) top[typeArrayName].push(item)
            // remove when working
            if (this.isThingSubType(item.type)) {
              if (top && top.Thing === undefined) top.Thing = []
              if (top) top.Thing.push(item)
            }
          }
        })
      return top ? top : arr.length === 1 ? arr[0] : arr
    }

    //revised to always return array and to match all
    assembleFromArray2(arr: PossibleBusinessObject[]) {
      const unsortedInstance = arr.map((item: PossibleBusinessObject) => this.getInstance(item)).filter((item) => item !== undefined) as ValidBusinessObject[]
      const sortedInstances = unsortedInstance.sort((a: ValidBusinessObject, b: ValidBusinessObject) => (a.name < b.name ? 0 : 1))
      let idMap: { [k: string]: ValidBusinessObject } = {}
      let parentsMap: { [k: string]: ValidBusinessObject } = {}
      sortedInstances.forEach((item: ValidBusinessObject) => {
        idMap[item.sk] = item
        if (item.pk === item.sk) parentsMap[item.sk] = item
      })
      const keys = Object.keys(idMap)
      debug && console.log('Keys: ', keys)
      sortedInstances.forEach((item: ValidBusinessObject) => {
        if (item.sk !== item.pk && keys.includes(item.pk)) {
          const parent = idMap[item.pk]
          const typeArrayName = item.type
          if (parent[typeArrayName] === undefined) {
            parent[typeArrayName] = []
          }
          parent[typeArrayName].push(item)
        } /*if (item.sk != this.getSafeId(item))*/ else {
          parentsMap[item.sk] = item
        }
      })
      debug && console.log('Parents Map', parentsMap)
      return Object.values(parentsMap)
    }

    // May or may not have the same effect as assembleFromArray2 but without sort and get intance
    parent(objectArr: PossibleBusinessObject[]) {
      let skMap: { [key: string]: PossibleBusinessObject } = {}
      objectArr.forEach((o) => {
        if (o.sk) skMap[o.sk] = o
      })
      debug && console.log('Map', skMap)
      objectArr.forEach((o) => {
        if (o.sk && o.pk && o.sk !== o.pk && o.type && skMap[o.pk]) {
          let t = skMap[o.pk]
          const typeArrayName = o.type
          if (!t[typeArrayName]) t[typeArrayName] = []
          if (t[typeArrayName].findIndex((tx: ValidBusinessObject) => tx && tx.sk && tx.sk === o.sk) === -1) {
            t[typeArrayName].push(o)
            delete skMap[o.sk]
          }

          // remove when working
          if (this.isThingSubType(o.type)) {
            if (!t.Thing) t.Thing = []
            if (t.Thing.findIndex((tx: ValidBusinessObject) => tx && tx.sk && tx.sk === o.sk) === -1) {
              t.Thing.push(o)
              delete skMap[o.sk]
            }
          }
        }
      })
      return Object.values(skMap)
    }
  }

export { createBusinessObjectAssembler }
